import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Gift, Loader2, Trash2 } from "lucide-react";

export default function AdminFreeAccess() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/free-access").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter a student email"); return; }
    setGranting(true);
    try {
      await api.post("/admin/free-access", { email: email.trim().toLowerCase() });
      toast.success(`Free access granted to ${email.trim()}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to grant access");
    } finally { setGranting(false); }
  };

  const remove = async (targetEmail) => {
    setRemoving(targetEmail);
    try {
      await api.delete(`/admin/free-access/${encodeURIComponent(targetEmail)}`);
      toast.success(`Free access removed for ${targetEmail}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove access");
    } finally { setRemoving(null); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="label-mono">Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-2">Free Access</h1>
        <p className="text-[#52525B] mt-2">Waive the assessment fee for specific email addresses.</p>

        <form onSubmit={grant} className="mt-8 card-brutal p-6 bg-[#A7F3D0] flex flex-col sm:flex-row gap-3">
          <input
            data-testid="free-access-email-input"
            type="email"
            required
            placeholder="student@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button type="submit" disabled={granting} data-testid="free-access-grant-btn" className="btn-brutal bg-blue-600 text-white px-5 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {granting ? <Loader2 className="animate-spin" size={16} /> : <Gift size={16} strokeWidth={2.5} />} Grant Free Access
          </button>
        </form>

        <div className="mt-10">
          <span className="label-mono">Granted</span>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1 mb-4">Emails with free access</h2>
          <div className="card-brutal overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FEF08A] border-b-2 border-[#0A0A0A]">
                <tr>
                  <th className="text-left px-4 py-3 label-mono">Email</th>
                  <th className="text-left px-4 py-3 label-mono">Granted by</th>
                  <th className="text-left px-4 py-3 label-mono">Granted on</th>
                  <th className="text-right px-4 py-3 label-mono">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className="px-4 py-6 label-mono">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="4" data-testid="no-free-access" className="px-4 py-6 text-[#52525B]">No free-access grants yet.</td></tr>
                ) : (
                  items.map((g) => (
                    <tr key={g.id} data-testid={`free-access-row-${g.id}`} className="border-b border-[#0A0A0A]/10 hover:bg-[#FAFAF9]">
                      <td className="px-4 py-3 font-semibold">{g.email}</td>
                      <td className="px-4 py-3 text-[#52525B]">{g.granted_by || "—"}</td>
                      <td className="px-4 py-3 text-[#52525B]">{g.created_at ? format(new Date(g.created_at), "dd MMM yyyy, h:mm a") : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => remove(g.email)}
                          disabled={removing === g.email}
                          data-testid={`remove-free-access-${g.id}`}
                          className="btn-brutal bg-white px-3 py-2 text-xs flex items-center gap-1 ml-auto disabled:opacity-60"
                        >
                          {removing === g.email ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} strokeWidth={2.5} />} Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
